import React, { useCallback, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import styled from '@emotion/styled';
import {
  spacing,
  breakpoints,
  palette,
  fontSize,
  Typography,
  Button,
  fontStyle,
} from '@playdapp/ui';
import { css } from '@emotion/react';
import { shallowEqual } from 'react-redux';
import { useInfiniteQuery } from 'react-query';
import { useInView } from 'react-intersection-observer';
import { useRouter } from 'next/router';
import times from 'lodash/times';

import getMyItemsData from 'api/graphql/getMyAssets';
import { bridgeReceive, bridgeSend } from 'api/bridge';
import useToastMessage from 'hooks/useToastMessage';
import useOpenControl from 'hooks/useOpenControl';
import { chainIdToMainNetworkName } from 'lib/network';
import { getBreakpointQuery } from 'lib/util';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearTxStatus, transferAsset } from 'store/marketplace';
import {
  setFirstCategory,
  setSelectedItems,
  setSelectedMyItemsCollection,
} from 'store/my-account';
import { setIsMergeOpen } from 'store/merge';
import type { Collection } from 'types/collections';

import SortByDropDown from '@/components/SortByDropDown';
import Icon from '@/components/Icon';
import Card from '@/components/Card';
import EmptyItem from '@/components/EmptyItem';
import Image from '@/components/Image';

import BridgeSendModal from '@/components/Modal/BridgeSendModal';
import BridgeReceiveModal from '@/components/Modal/BridgeReceiveModal';
import TransactionModal from '@/components/Modal/TransactionModal';
import TransactionResultModal from '@/components/Modal/TransactionResultModal';
import ErrorContents from '@/components/ErrorContents';
import MetaTag from '@/components/MetaTag';
import Merge from '@/components/Merge';

const PageBlock = styled.div<{ isMergeOpen: boolean }>`
  ${({ isMergeOpen }) =>
    isMergeOpen &&
    css`
      touch-action: none;
    `};
`;

const ItemOptionButtonWrapper = styled.div<{
  isMdDown: boolean;
  isBetween: string;
}>`
  position: ${({ isMdDown }) => isMdDown && 'sticky'};
  top: 64px;
  display: ${({ isMdDown }) => (isMdDown ? 'block' : 'flex')};
  justify-content: ${({ isBetween }) =>
    isBetween === 'space-between' ? 'space-between' : 'flex-end'};
  align-items: center;
  height: 100%;
  gap: ${spacing.s};
  border-radius: 8px;
  order: 1;
  z-index: 1;

  ${breakpoints.down('md')} {
    width: 100%;
  }
`;

const ItemReserves = styled.div`
  ${fontStyle('b4')};
  text-align: left;
  color: ${palette.dgray400};

  ${breakpoints.down('lg')} {
    width: 100%;
    max-width: ${({ theme }) => theme.rem(100)};
    margin-top: ${spacing.xl};
  }

  ${breakpoints.down('md')} {
    margin-top: ${spacing.m};
  }
`;

const CustomButton = styled(Button)<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${spacing.xxxl};
  max-height: ${({ theme }) => theme.rem(48)};
  white-space: pre;
  border-radius: ${spacing.xs};
  background-color: white;

  :hover {
    background-color: ${({ disabled }) =>
      !disabled && 'rgba(43, 202, 176, 0.08)'};
  }

  & + a,
  a + & {
    margin-left: ${spacing.xs};
  }

  ${breakpoints.down('md')} {
    width: 100%;
    height: ${spacing.xxxl};
    font-size: ${fontSize.p4};
    padding: ${spacing.xs} ${spacing.s};
  }
`;

const SelectBundle = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => theme.rem(48)};

  .bundle-count {
    width: ${spacing.xl};
    height: ${spacing.xl};
    padding-top: ${spacing.xxs};
    margin-left: ${spacing.xxs};
    background-color: ${palette.white};
    color: ${palette.marketplace};
    border-radius: 50%;
  }

  & > span {
    margin-left: ${spacing.s} !important;
  }

  ${breakpoints.down('md')} {
    min-width: ${({ theme }) => theme.rem(65)};
    height: 100%;
    font-size: ${fontSize.b3};
    padding: ${spacing.s} ${spacing.xs};

    & > input {
      margin-right: ${spacing.xs};
    }
  }
`;

const DropDownSortContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: ${({ theme }) => theme.rem(500)};
  border-radius: ${spacing.xs};
  padding: 0;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`;

const DropDownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${spacing.s} ${spacing.l};
  outline: 0;
  border-radius: ${spacing.xs};
  background-color: transparent;
  text-align: left;
  font-family: initial;
  white-space: pre-line;

  &:hover {
    background-color: ${palette.gray400};
  }

  & > span {
    flex: none;
    border-radius: 50%;
  }

  & > p {
    margin-left: ${spacing.s};
  }

  ${breakpoints.down('md')} {
    padding: ${spacing.s};
  }
`;

const ContentBody = styled.div`
  width: 100%;
  min-height: ${({ theme }) => theme.rem(540)};
`;

const CardListBox = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${spacing.xl};
  margin-top: ${spacing.m};

  ${breakpoints.down('xxl')} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  ${breakpoints.down('xl')} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${breakpoints.down('lg')} {
    margin-top: ${spacing.l};
  }

  ${breakpoints.down('md')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto;
    gap: ${spacing.xs};
  }

  @media screen and (max-width: 359px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${spacing.s};
  }
`;

const EmptyItemBlock = styled.div`
  margin-top: 10rem;
`;

const FloatingButtonBlock = styled.div<{ isSelected: boolean }>`
  position: fixed;
  bottom: 3rem;
  left: 50%;
  z-index: 1000;
  transform: translateX(-50%);

  button {
    color: ${({ isSelected }) =>
      isSelected ? palette.white : palette.marketplace};

    ${({ isSelected }) =>
      !isSelected &&
      css`
        border: 1px solid ${palette.marketplace};
      `}

    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  }

  ${breakpoints.down('md')} {
    padding: 0 ${spacing.xl};

    &,
    button {
      width: 100%;
    }
  }
`;

const NetworkStatusNotice = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: ${spacing.xxl};
  padding: 0 10px;
  font-size: 14px;
  color: ${palette.yellow700};
  background-color: ${palette.yellow100};
  gap: 10px;
  border-radius: ${spacing.xs};

  ${breakpoints.down('md')} {
    display: none;
  }
`;

const CenterNavigation = styled.div`
  display: flex;
  position: sticky;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 1rem;
  z-index: 2;

  ${breakpoints.down('md')} {
    padding: 0;
  }
`;

const CategoryListBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CollectionListDropDown = styled(SortByDropDown)<{
  isClose: boolean;
}>`
  position: relative;
  align-items: flex-end;
  ${fontStyle('p3')};

  & > div {
    color: ${palette.dgray400};
    border-radius: 10px;

    :hover {
      background-color: ${palette.gray100};
    }
    transition: 0.3s;

    ${breakpoints.down('md')} {
      width: 100%;
      white-space: pre;
    }
  }

  ${breakpoints.down('md')} {
    width: 100%;
  }
`;

const MobileNetworkStatusNotice = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 6px 10px;
  margin-top: ${spacing.l};
  gap: 10px;
  ${fontStyle('p4')};
  color: ${palette.yellow700};
  background-color: ${palette.yellow100};

  ${breakpoints.up('md')} {
    display: none;
  }
`;

const MyItems = () => {
  const isMdDownScreen = useMedia(getBreakpointQuery(breakpoints.down('md')));
  const isLgDownScreen = useMedia(getBreakpointQuery(breakpoints.down('lg')));

  const router = useRouter();
  const dispatch = useAppDispatch();

  const [isClose, setIsClose] = useState(false);
  const [isSendClicked, setIsSendClicked] = useState(false);
  const [isReceiveClicked, setIsReceiveClicked] = useState(false);
  const [isReceivable, setIsReceivable] = useState(false);
  const [receivableItems, setReceivableItems] = useState([]);
  const [myItemCount, setMyItemCount] = useState(0);
  const [selectMode, setSelectMode] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [transactionModalStatus, setTransactionModalStatus] = useState<
    string | null
  >(null);

  const { selectedItems, selectedMyItemsCollection, firstCategory } =
    useAppSelector(({ myAccount }) => myAccount, shallowEqual);
  const { txStatus, transactionHash } = useAppSelector(
    ({ marketplace }) => marketplace,
  );
  const { collectionList } = useAppSelector(({ collections }) => collections);
  const { account, activeChainId } = useAppSelector(({ wallet }) => wallet);
  const { prev } = useAppSelector(({ page }) => page);
  const { isMergeOpen } = useAppSelector(({ merge }) => merge);

  const [isBridgeSendModalOpen, handleBridgeSendModalOpen] = useOpenControl();
  const [isBridgeReceiveModalOpen, handleBridgeReceiveModalOpen] =
    useOpenControl();
  const [isTransactionResultModalOpen, handleTransactionResultModalOpen] =
    useOpenControl();
  const [isTransactionModalOpen, handleTransactionModalOpen] = useOpenControl();

  const [loadMoreRef, loadMoreRefInView] = useInView();
  const { handleUpdateToast, handleCloseToast } = useToastMessage({
    position: 'top',
    duration: 5000,
  });

  const toggleSelectMode = (
    e?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.preventDefault();

    if (selectMode === 'sell') {
      dispatch(setSelectedItems([]));
      dispatch(setFirstCategory(null));
      setSelectMode(null);
      return;
    }

    dispatch(setSelectedItems([]));
    setSelectMode('sell');
    dispatch(setIsMergeOpen(false));
  };

  const toggleTransferSuccessModal = (isOpen: boolean) => () => {
    handleTransactionResultModalOpen(isOpen);
    handleTransactionModalOpen(false);
    refetch();
    dispatch(clearTxStatus());
  };

  const moveToSellPage = () => {
    if (!activeChainId || !selectedItems.length) {
      return;
    }
    if (selectedItems.length === 1) {
      router.push(
        `/sell/${chainIdToMainNetworkName[activeChainId]}/${selectedItems[0].assetAddress}/${selectedItems[0].tokenId}`,
      );
      dispatch(setSelectedItems([]));
      dispatch(setFirstCategory(null));
      return;
    }
    router.push('/sell/bundle');
  };

  const handleSendBridge = async () => {
    try {
      if (selectedItems.length === 0 || !account) {
        return;
      }
      const { tokenId, assetAddress } = selectedItems[0];

      setIsSendClicked(true);
      await bridgeSend({ tokenId });

      const auction = await dispatch(
        transferAsset({
          from: account,
          to: '0xd27882a9815DBBD54DBdc937B62f000143Bd53c1',
          tokenId,
          tokenAddress: assetAddress,
        }),
      );

      if (auction.payload) {
        setTxHash(auction.payload as string);
        handleTransactionResultModalOpen(true);
        dispatch(setSelectedItems([]));
        refetch();
      }
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(err);
    } finally {
      setIsSendClicked(false);
      handleBridgeSendModalOpen(false);
    }
  };

  const handleReceiveBridge = async () => {
    try {
      setIsReceiveClicked(true);
      const res = await bridgeReceive();

      if (res.data) {
        setTimeout(() => {
          handleUpdateToast(
            `Your ${
              res.data.length > 1 ? `[${res.data.length}] NFTs have` : `NFT has`
            }  been transferred successfully`,
            'success',
            true,
          );
        }, 2000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsReceiveClicked(false);
      handleBridgeReceiveModalOpen(false);
      setIsReceivable(false);
      setReceivableItems([]);
    }
  };

  const fetchList = async ({ pageParam = 1 }) => {
    if (!account || !activeChainId) {
      throw new Error('No account or chainId');
    }

    const res = await getMyItemsData({
      from: (pageParam - 1) * 30,
      count: 30,
      andOperations: [
        { key: 'order.sale', value: '$not_exist' },
        { key: 'order.auction', value: '$not_exist' },
        { key: 'owner', value: account },
        { key: 'network', value: chainIdToMainNetworkName[activeChainId] },
      ],
      orOperations: [
        selectedMyItemsCollection
          ? selectedMyItemsCollection.assets.map((id) => ({
              key: 'assetId',
              value: id.toString(),
            }))
          : [],
      ],
      sortBy: 'Transfer',
      sortAscending: false,
    });

    return { ...res, from: (pageParam - 1) * 30, nextPage: pageParam + 1 };
  };

  const { isLoading, data, hasNextPage, error, fetchNextPage, refetch } =
    useInfiniteQuery(
      ['myitems', account, selectedMyItemsCollection],
      fetchList,
      {
        getNextPageParam: (lastPage) => {
          if (lastPage.total > lastPage.from + 30) {
            return lastPage.nextPage;
          }
        },
        enabled: !!account && !!activeChainId,
        refetchOnWindowFocus: false,
      },
    );

  const handleNextPage = useCallback(async () => {
    hasNextPage && (await fetchNextPage());
  }, [fetchNextPage, hasNextPage]);

  const handleCollectionItemClick = (categoryId: number) => () => {
    if (!categoryId) {
      dispatch(setSelectedMyItemsCollection(null));
    }
    if (activeChainId) {
      const activeCollection = collectionList[
        chainIdToMainNetworkName[activeChainId]
      ].find((category) => category.categoryId === categoryId);

      if (activeCollection) {
        dispatch(setSelectedMyItemsCollection(activeCollection));
      }
      setIsClose(true);
    }
  };

  const handleCardClick =
    (
      tokenId?: string,
      collectionInfo?: Collection,
      image?: string | null,
      title?: string | null,
      attributes?: { [key: string]: number | string | null }[],
      assetAddress?: string | null,
      isDisbaled?: boolean,
    ) =>
    () => {
      if (!tokenId || !activeChainId) {
        return;
      }

      if (!selectMode || isDisbaled) {
        return;
      }

      const checkSameTokenId = selectedItems.find(
        (list) => list.tokenId === tokenId,
      );

      if (checkSameTokenId) {
        const deletedOverlapItem = selectedItems.filter(
          (list) => list.tokenId !== tokenId,
        );
        dispatch(setSelectedItems(deletedOverlapItem));
        return;
      }

      if (selectedItems.length >= 30) {
        handleUpdateToast(
          'You can select a maximum of 30 items.',
          'error',
          true,
        );

        return;
      }

      if (
        chainIdToMainNetworkName[activeChainId] === 'solana' &&
        selectedItems.length >= 1
      ) {
        handleUpdateToast('Bundle sell is coming soon.', 'error', true);
        return;
      }

      if (checkSameTokenId === undefined && selectedItems.length < 30) {
        const selectedItemData = {
          tokenId,
          imageUrl: image ?? '',
          name: title ?? '',
          assetAddress: assetAddress ?? '',
          attributes: attributes ?? [],
        };

        if (selectMode === 'send') {
          dispatch(setSelectedItems([selectedItemData]));
          handleBridgeSendModalOpen(true);
          return;
        }

        collectionInfo &&
          firstCategory !== collectionInfo.title &&
          dispatch(setFirstCategory(collectionInfo.title));

        dispatch(setSelectedItems([...selectedItems, selectedItemData]));
      }
    };

  const handleToggleMerge = () => {
    dispatch(setIsMergeOpen(!isMergeOpen));

    if (selectMode === 'sell') {
      dispatch(setSelectedItems([]));
      dispatch(setFirstCategory(null));
      setSelectMode(null);
    }
  };

  useEffect(
    () => () => {
      handleCloseToast();
    },
    [handleCloseToast],
  );

  useEffect(() => {
    if (isClose) {
      setIsClose(() => false);
    }
  }, [isClose]);

  useEffect(() => {
    if (loadMoreRefInView) {
      handleNextPage();
    }
  }, [loadMoreRefInView, handleNextPage]);

  useEffect(() => {
    if (selectedItems.length === 0) {
      selectMode !== 'send' && dispatch(setFirstCategory(null));
    } else {
      !selectMode && firstCategory && setSelectMode('sell');
    }
  }, [selectedItems, dispatch, firstCategory, selectMode]);

  useEffect(() => {
    if (!prev.startsWith('/item/') && !prev.startsWith('/my-page/items')) {
      dispatch(setSelectedItems([]));
      dispatch(setFirstCategory(null));
    }
  }, [account, activeChainId, prev, dispatch]);

  useEffect(() => {
    switch (txStatus) {
      case 'INITIALIZE_PROXY':
      case 'APPROVE_ALL_ASSETS':
      case 'APPROVE_ASSET':
      case 'TRANSFER_ALL':
        setTransactionModalStatus(txStatus);
        handleTransactionModalOpen(true);
        break;
      case 'TX_SUCCESS':
        handleTransactionResultModalOpen(true);
        setTransactionModalStatus('');
        break;
      case 'TX_DENIED':
        setTransactionModalStatus('');
        break;
      case 'TX_FAILED':
        setTransactionModalStatus('');
        break;
      case 'SET_PENDING_TRANSACTION_HASH':
        break;
      default:
        break;
    }
  }, [handleTransactionResultModalOpen, handleTransactionModalOpen, txStatus]);

  useEffect(() => {
    if ('merge' in router.query) {
      dispatch(setIsMergeOpen(true));
    } else {
      dispatch(setIsMergeOpen(false));
    }

    return () => {
      dispatch(setIsMergeOpen(false));
    };
  }, [dispatch, router.query]);

  useEffect(() => {
    if (data) {
      data.pages.map((info) => setMyItemCount(info.total));
    }
  }, [data]);

  return (
    <PageBlock isMergeOpen={isMergeOpen}>
      <MetaTag title="My Items | PlayDapp" />

      <ItemOptionButtonWrapper
        isMdDown={isMdDownScreen}
        isBetween="space-between"
      >
        {!isLgDownScreen && (
          <ItemReserves>
            {myItemCount ? myItemCount?.toLocaleString() : 0} items
          </ItemReserves>
        )}

        <NetworkStatusNotice>
          <Icon
            name="warning-yellow"
            alt="warning-yellow"
            width={24}
            height={24}
          />
          <p>
            NFT status may take a few minutes to update depending to network
            congestion
          </p>
        </NetworkStatusNotice>
        <CenterNavigation>
          <CustomButton
            color="primary"
            size="xs"
            variant="outline"
            disabled={!data?.pages[0].assets.length}
            onClick={toggleSelectMode}
          >
            <Typography
              color={!data?.pages[0].assets.length ? 'dgray200' : 'primary700'}
              type="p3"
            >
              Sell Bundle
            </Typography>
          </CustomButton>

          <CollectionListDropDown
            title={selectedMyItemsCollection?.title ?? 'All Collection'}
            width="262"
            height="36"
            top="46"
            isBorder
            isDropBox
            floatingContent
            isClose={isClose}
          >
            <DropDownSortContent>
              <CategoryListBlock>
                <DropDownItem onClick={handleCollectionItemClick(0)}>
                  <Image
                    prefix="s3"
                    src="/collections/all.png"
                    alt="all"
                    width={24}
                    height={24}
                  />
                  <Typography type="p3" color="dgray400" component="p">
                    All Collection
                  </Typography>
                </DropDownItem>

                {activeChainId &&
                  collectionList[chainIdToMainNetworkName[activeChainId]].map(
                    (category) => (
                      <DropDownItem
                        key={category.categoryId}
                        onClick={handleCollectionItemClick(category.categoryId)}
                      >
                        <Image
                          src={category.thumbnailUrl}
                          alt="collection"
                          width={24}
                          height={24}
                        />
                        <Typography type="p4" color="dgray400" component="p">
                          {category.title}
                        </Typography>
                      </DropDownItem>
                    ),
                  )}
              </CategoryListBlock>
            </DropDownSortContent>
          </CollectionListDropDown>
        </CenterNavigation>
      </ItemOptionButtonWrapper>

      {!isMdDownScreen && isLgDownScreen && (
        <ItemReserves>
          {myItemCount ? myItemCount?.toLocaleString() : 0} items
        </ItemReserves>
      )}

      <MobileNetworkStatusNotice>
        <Icon
          name="warning-yellow"
          alt="warning-yellow"
          width={24}
          height={24}
        />
        <p>
          NFT status may take a few minutes to update depending to network
          congestion
        </p>
      </MobileNetworkStatusNotice>

      {isMdDownScreen && (
        <ItemReserves>
          {myItemCount ? myItemCount?.toLocaleString() : 0} items
        </ItemReserves>
      )}

      <ContentBody>
        {(!isMergeOpen || isMdDownScreen) && (
          <>
            <CardListBox>
              {isLoading &&
                times(30, (index) => <Card key={index} isLoading />)}

              {data?.pages.map((items) => {
                const { assets } = items;

                return Object.values(assets).map(
                  (
                    {
                      name,
                      imageUri,
                      animationUri,
                      tokenId,
                      network,
                      assetAddress,
                      owner,
                      order,
                      collectionInfo,
                      attributes,
                      likeCount,
                    },
                    index,
                  ) => (
                    <Card
                      key={`${name}_${tokenId}-${index}`}
                      collectionInfo={collectionInfo}
                      imageUri={imageUri}
                      animationUri={animationUri}
                      tokenId={tokenId}
                      name={name}
                      order={order}
                      network={network}
                      href={`/item/${network}/${assetAddress}/${tokenId}`}
                      assetAddress={assetAddress}
                      attributes={attributes}
                      isOwner={owner === account}
                      isSelectable={!!selectMode}
                      like={likeCount}
                      isActive={
                        !!selectMode &&
                        selectedItems.some((item) => item.tokenId === tokenId)
                      }
                      disabled={
                        (selectMode === 'sell' &&
                          firstCategory !== null &&
                          collectionInfo?.title !== firstCategory) ||
                        (selectMode === 'send' && !name?.includes('SSR'))
                      }
                      onClick={handleCardClick}
                    />
                  ),
                );
              })}

              {hasNextPage && (
                <>
                  <Card isLoading ref={loadMoreRef} />
                  {times(9, (index) => (
                    <Card key={index} isLoading />
                  ))}
                </>
              )}
            </CardListBox>

            {!isLoading && error && (
              <EmptyItemBlock>
                <ErrorContents />
              </EmptyItemBlock>
            )}

            {!error &&
              data?.pages &&
              data?.pages.length > 0 &&
              data?.pages[0].total === 0 && (
                <EmptyItemBlock>
                  <EmptyItem />
                </EmptyItemBlock>
              )}

            {selectMode === 'sell' && selectedItems.length > 0 && (
              <FloatingButtonBlock isSelected={selectMode === 'sell'}>
                <SelectBundle onClick={moveToSellPage}>
                  <Icon name="grid-white" size={18} />
                  <span>Sell Bundle of</span>
                  <span className="bundle-count">{selectedItems.length}</span>
                </SelectBundle>
              </FloatingButtonBlock>
            )}
          </>
        )}

        {isMergeOpen && <Merge handleToggleMerge={handleToggleMerge} />}
      </ContentBody>

      {activeChainId &&
        chainIdToMainNetworkName[activeChainId] === 'ethereum' &&
        isBridgeSendModalOpen &&
        selectedItems.length > 0 && (
          <BridgeSendModal
            isOpen={isBridgeSendModalOpen}
            handleOpen={handleBridgeSendModalOpen}
            handleSend={handleSendBridge}
            image={selectedItems[0].imageUrl}
            isSendClicked={isSendClicked}
          />
        )}

      {activeChainId &&
        chainIdToMainNetworkName[activeChainId] === 'polygon' && (
          <BridgeReceiveModal
            isOpen={isBridgeReceiveModalOpen}
            handleOpen={handleBridgeReceiveModalOpen}
            handleReceive={handleReceiveBridge}
            isClicked={isReceiveClicked}
            isAvailable={isReceivable}
            items={receivableItems}
          />
        )}

      {isTransactionModalOpen && (
        <TransactionModal
          isOpen={isTransactionModalOpen}
          handleOpen={handleTransactionModalOpen}
          network="ethereum"
          status={transactionModalStatus}
          shouldCloseOnOverlayClick={false}
          stepTypeSubFix="transfer"
          txHash={transactionHash}
        />
      )}

      {isTransactionResultModalOpen && (
        <TransactionResultModal
          isOpen={isTransactionResultModalOpen}
          handleOpen={toggleTransferSuccessModal(false)}
          type="success"
          network="ethereum"
          txHash={txHash}
        />
      )}
    </PageBlock>
  );
};

export default MyItems;
